
import { getLanguageBreakdown } from './textProcessing';

export type LanguageMode = 'english' | 'chinese' | 'both';

export interface LanguageValidationResult {
  isValid: boolean;
  message?: string;
  suggestedMode?: LanguageMode;
}

/**
 * Validates if the selected language mode has available content
 */
export const validateLanguageContent = (
  text: string,
  selectedLanguage: LanguageMode
): LanguageValidationResult => {
  console.debug('🔍 [validateLanguageContent] Called with:', {
    textLength: text?.length || 0,
    selectedLanguage,
    textPreview: text?.substring(0, 50) || 'No text'
  });

  if (!text || text.trim().length === 0) {
    console.debug('❌ [validateLanguageContent] No text provided');
    return {
      isValid: false,
      message: 'No content available to read.'
    };
  }

  console.debug('🔍 [validateLanguageContent] Getting language breakdown...');
  const breakdown = getLanguageBreakdown(text);
  
  console.debug('🔍 [validateLanguageContent] Language breakdown:', {
    hasEnglish: breakdown.hasEnglish,
    hasChinese: breakdown.hasChinese,
    english: breakdown.english,
    chinese: breakdown.chinese
  });
  
  // Both mode is always valid if there's any content
  if (selectedLanguage === 'both') {
    console.debug('✅ [validateLanguageContent] Both mode selected - always valid');
    return { isValid: true };
  }
  
  // Check English content
  if (selectedLanguage === 'english') {
    console.debug('🔍 [validateLanguageContent] Checking English content...');
    if (!breakdown.hasEnglish) {
      console.debug('❌ [validateLanguageContent] No English content found');
      const result = {
        isValid: false,
        message: 'No English content detected in this prayer.',
        suggestedMode: breakdown.hasChinese ? 'chinese' as LanguageMode : 'both' as LanguageMode
      };
      console.debug('❌ [validateLanguageContent] Returning validation failure:', result);
      return result;
    }
    console.debug('✅ [validateLanguageContent] English content found - valid');
    return { isValid: true };
  }
  
  // Check Chinese content
  if (selectedLanguage === 'chinese') {
    console.debug('🔍 [validateLanguageContent] Checking Chinese content...');
    if (!breakdown.hasChinese) {
      console.debug('❌ [validateLanguageContent] No Chinese content found');
      const result = {
        isValid: false,
        message: 'No Chinese content detected in this prayer.',
        suggestedMode: breakdown.hasEnglish ? 'english' as LanguageMode : 'both' as LanguageMode
      };
      console.debug('❌ [validateLanguageContent] Returning validation failure:', result);
      return result;
    }
    console.debug('✅ [validateLanguageContent] Chinese content found - valid');
    return { isValid: true };
  }
  
  console.debug('✅ [validateLanguageContent] Default case - valid');
  return { isValid: true };
};

/**
 * Gets a user-friendly message for language mode
 */
export const getLanguageModeLabel = (mode: LanguageMode): string => {
  console.debug('🔍 [getLanguageModeLabel] Called with mode:', mode);
  switch (mode) {
    case 'english': return 'English';
    case 'chinese': return 'Chinese';
    case 'both': return 'Both Languages';
    default: return 'Both Languages';
  }
};
